/* ======================================================================
   PRODUCT PAGE — R1 / R6 / R10 (DRAFT)
   ----------------------------------------------------------------------
   Loaded only on product templates, from layout/theme.liquid.

     R1   material selector: prefetch, switching feedback, scroll
          restoration, and the safeguards that stop any of those from
          leaving the page in a broken state
     R6   deferred video hydration
     R10  Klaviyo scroll-lock repair, alt backfill

   The material selector's architecture is untouched. Every card is still
   a plain <a> to a separate product, and every behaviour below is
   progressive: if this file fails to load, the links still work exactly
   as they do today.

   Reverting: delete this file and its script tag in layout/theme.liquid.
   ====================================================================== */

(function () {
  'use strict';

  var SCROLL_KEY = 'klv:pdp-return';
  var SCROLL_MAX_AGE = 30000;
  var SWITCH_TIMEOUT = 8000;

  var reducedMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function onReady(fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    } else {
      fn();
    }
  }

  /* --------------------------------------------------------------------
     Shared helpers
     ------------------------------------------------------------------ */

  // A stable identifier for one material family. Every sibling product in
  // the family renders the same set of hrefs, so the key matches across a
  // switch and cannot collide with an unrelated product.
  function familyKey(root) {
    var links = root.querySelectorAll('.option-groups__card');
    var hrefs = [];

    for (var i = 0; i < links.length; i++) {
      var href = links[i].getAttribute('href');
      if (href) hrefs.push(href.split('?')[0]);
    }

    return hrefs.sort().join('|');
  }

  function saveData() {
    var c = navigator.connection;
    if (!c) return false;
    return Boolean(c.saveData) || /(^|-)2g$/.test(c.effectiveType || '');
  }

  /* ====================================================================
     R1 — MATERIAL SELECTOR
     ==================================================================== */

  function initMaterialSelector() {
    var groups = document.querySelectorAll('.option-groups');
    if (!groups.length) return;

    var root = groups[0].closest('.product-info') || document.body;
    var key = familyKey(document);

    /* ---- Prefetch, on intent only ---------------------------------- */
    if (!saveData()) {
      var prefetched = {};

      var prefetch = function (href) {
        if (!href || prefetched[href]) return;
        prefetched[href] = true;

        var link = document.createElement('link');
        link.rel = 'prefetch';
        link.href = href;
        link.as = 'document';
        document.head.appendChild(link);
      };

      document.querySelectorAll('.option-groups__card').forEach(function (card) {
        if (card.classList.contains('is-selected')) return;

        var warm = function () {
          prefetch(card.getAttribute('href'));
        };

        card.addEventListener('pointerenter', warm, { once: true });
        card.addEventListener('touchstart', warm, { once: true, passive: true });
        card.addEventListener('focus', warm, { once: true });
      });
    }

    /* ---- Radiogroup keyboard behaviour ------------------------------
       The cards carry role="radio" from Liquid, but the roving tabindex
       is applied here rather than server-side: without JavaScript the
       cards must stay ordinary, individually tabbable links. */
    groups.forEach(function (group) {
      group.querySelectorAll('[role="radiogroup"]').forEach(function (list) {
        var cards = Array.prototype.slice.call(
          list.querySelectorAll('.option-groups__card')
        );
        if (cards.length < 2) return;

        var selectedIndex = cards.findIndex(function (c) {
          return c.getAttribute('aria-checked') === 'true';
        });
        if (selectedIndex < 0) selectedIndex = 0;

        cards.forEach(function (card, i) {
          card.tabIndex = i === selectedIndex ? 0 : -1;
        });

        list.addEventListener('keydown', function (event) {
          var current = cards.indexOf(document.activeElement);
          if (current < 0) return;

          var next = null;

          switch (event.key) {
            case 'ArrowRight':
            case 'ArrowDown':
              next = (current + 1) % cards.length;
              break;
            case 'ArrowLeft':
            case 'ArrowUp':
              next = (current - 1 + cards.length) % cards.length;
              break;
            case 'Home':
              next = 0;
              break;
            case 'End':
              next = cards.length - 1;
              break;
            default:
              return;
          }

          event.preventDefault();

          cards.forEach(function (c) {
            c.tabIndex = -1;
          });
          cards[next].tabIndex = 0;
          cards[next].focus();
        });
      });
    });

    /* ---- Switching feedback + scroll preservation ------------------- */
    document.querySelectorAll('.option-groups__card').forEach(function (card) {
      card.addEventListener('click', function (event) {
        // Let the browser handle anything that is not a plain navigation:
        // new tab, new window, download, right click.
        if (
          event.defaultPrevented ||
          event.button !== 0 ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }

        if (card.classList.contains('is-selected')) {
          event.preventDefault();
          return;
        }

        try {
          sessionStorage.setItem(
            SCROLL_KEY,
            JSON.stringify({ y: window.scrollY || 0, key: key, t: Date.now() })
          );
        } catch (e) {
          /* Private mode or a full quota. The switch still works. */
        }

        card.setAttribute('aria-busy', 'true');
        document.body.classList.add('klv-switching');

        // Error safeguard. If navigation is blocked, refused, or simply
        // slow past any reasonable wait, give the page back to the
        // customer rather than leaving it faded and unclickable.
        window.setTimeout(function () {
          document.body.classList.remove('klv-switching');
          card.removeAttribute('aria-busy');
        }, SWITCH_TIMEOUT);
      });
    });

    // Returning through the back button restores from bfcache with the
    // faded state still applied, so clear it there too.
    window.addEventListener('pageshow', function () {
      document.body.classList.remove('klv-switching');
      document.querySelectorAll('.option-groups__card[aria-busy]').forEach(function (c) {
        c.removeAttribute('aria-busy');
      });
    });

    /* ---- Restore scroll after a switch ------------------------------ */
    var stored = null;

    try {
      stored = JSON.parse(sessionStorage.getItem(SCROLL_KEY) || 'null');
      sessionStorage.removeItem(SCROLL_KEY);
    } catch (e) {
      stored = null;
    }

    if (
      stored &&
      stored.key === key &&
      typeof stored.y === 'number' &&
      stored.y > 0 &&
      Date.now() - stored.t < SCROLL_MAX_AGE &&
      (window.scrollY || 0) < 40
    ) {
      var target = stored.y;

      var restore = function () {
        // Never scroll past the end of a page that has not finished
        // laying out; clamp to whatever exists right now.
        var max = Math.max(
          0,
          document.documentElement.scrollHeight - window.innerHeight
        );
        window.scrollTo(0, Math.min(target, max));
      };

      requestAnimationFrame(function () {
        requestAnimationFrame(restore);
      });

      // The gallery and the reels rail settle after their images decode,
      // so correct once more when the load event has been and gone.
      window.addEventListener('load', function () {
        window.setTimeout(restore, 60);
      });
    }

    return root;
  }

  /* ====================================================================
     R6 — DEFERRED VIDEO
     ====================================================================
     Videos marked .klv-deferred-video carry their source in data-src and
     download nothing until they are actually on screen. */

  function initDeferredVideo() {
    var videos = document.querySelectorAll('video.klv-deferred-video');
    if (!videos.length) return;

    var hydrate = function (video) {
      if (video.dataset.src && !video.getAttribute('src')) {
        video.setAttribute('src', video.dataset.src);
        video.load();
      }
    };

    var play = function (video) {
      hydrate(video);
      if (reducedMotion) return;

      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') {
        attempt.catch(function () {
          /* Autoplay refused. The poster stays; controls still work. */
        });
      }
    };

    // The theme's own play/mute controls call play() directly, so make
    // sure a source exists before they do.
    videos.forEach(function (video) {
      var wrapper = video.parentElement;
      if (wrapper) {
        wrapper.addEventListener(
          'pointerdown',
          function () {
            hydrate(video);
          },
          { once: true }
        );
      }
    });

    if (!('IntersectionObserver' in window)) {
      videos.forEach(play);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          var video = entry.target;

          if (entry.isIntersecting) {
            play(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.25 }
    );

    videos.forEach(function (video) {
      observer.observe(video);
    });
  }

  /* ====================================================================
     R10 — KLAVIYO SCROLL LOCK
     ====================================================================
     body.klaviyo-prevent-body-scrolling sets `overflow: hidden !important`
     and was found applied on page load with no popup open. The class is
     left in place whenever a Klaviyo dialog genuinely exists, so the
     popup's own lifecycle is not interfered with — it is only released
     when nothing is actually open. */

  var KLAVIYO_LOCK = 'klaviyo-prevent-body-scrolling';

  function klaviyoDialogOpen() {
    var dialogs = document.querySelectorAll(
      '.klaviyo-form, [class*="kl-private-reset-css"] [role="dialog"], [data-testid="klaviyo-form"]'
    );

    for (var i = 0; i < dialogs.length; i++) {
      var el = dialogs[i];
      var style = window.getComputedStyle(el);

      if (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        el.getBoundingClientRect().height > 0
      ) {
        return true;
      }
    }

    return false;
  }

  function initKlaviyoLockRelease() {
    var release = function () {
      if (!document.body.classList.contains(KLAVIYO_LOCK)) return;
      if (klaviyoDialogOpen()) return;
      document.body.classList.remove(KLAVIYO_LOCK);
    };

    release();

    var pending = null;
    var observer = new MutationObserver(function () {
      window.clearTimeout(pending);
      // A short delay lets Klaviyo finish adding the class and the dialog
      // in the same tick before the state is judged.
      pending = window.setTimeout(release, 400);
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['class']
    });

    window.addEventListener('pageshow', release);
  }

  /* ====================================================================
     R10 — ALT BACKFILL
     ====================================================================
     Images that reach the page without an alt attribute are announced by
     filename. Product imagery takes the nearest real product title;
     anything with no title to inherit is marked decorative, which is the
     honest answer rather than a guessed description. */

  function initAltBackfill() {
    var scopes = document.querySelectorAll(
      '.product-card, .product-gallery, .klv-pairing, .reels__product-image, main img'
    );
    if (!scopes.length) return;

    document.querySelectorAll('main img:not([alt])').forEach(function (img) {
      var card = img.closest('.product-card, .klv-pairing, .reels__product-card, .swiper-slide');
      var titleEl = card
        ? card.querySelector('.product-title, .product__title, .klv-pairing__title')
        : null;
      var title = titleEl ? titleEl.textContent.trim() : '';

      if (!title) {
        var main = document.querySelector('.shopify-section--main-product .product-title');
        if (main && img.closest('.product-gallery')) title = main.textContent.trim();
      }

      img.setAttribute('alt', title || '');
    });
  }

  /* ====================================================================
     R2 — ZOOM BUTTON
     ====================================================================
     Restoring the button's visibility was only half the fix. The theme's
     <open-lightbox-button> is defined, but it drives a <product-lightbox>
     element that does not exist in this theme, so the button stayed inert
     even once visible.

     The gallery images are already bound to Fancybox (each media is an
     <a data-fancybox="gallery">) and clicking one opens the lightbox
     correctly. The button now opens the lightbox on whichever media is
     currently in view, which is what a customer expects of it. */

  function initZoomButton() {
    var button = document.querySelector(
      '.shopify-section--main-product .product-gallery__zoom-button'
    );
    var gallery = document.querySelector('.shopify-section--main-product product-gallery');
    if (!button || !gallery) return;

    var links = gallery.querySelectorAll('.product-gallery__media a[data-fancybox]');

    if (!links.length) {
      // Nothing to zoom into — better no control than a dead one.
      button.hidden = true;
      return;
    }

    button.addEventListener('click', function (event) {
      event.preventDefault();
      event.stopPropagation();

      // Whichever media currently covers the middle of the viewport.
      var midpoint = window.innerHeight / 2;
      var best = links[0];
      var bestDistance = Infinity;

      links.forEach(function (link) {
        var box = link.getBoundingClientRect();
        var distance = Math.abs(box.top + box.height / 2 - midpoint);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = link;
        }
      });

      best.click();
    });
  }

  /* ====================================================================
     R7 / R3 — COLLAPSE SECTIONS THAT RESOLVE TO NOTHING
     ====================================================================
     An app section can render a wrapper with real padding around a widget
     that then decides it has nothing to show. The Loox reviews section
     does exactly that today: the app block is present but 0px tall,
     leaving ~129px of empty page. CSS cannot catch it, because the block
     is not :empty — it holds an iframe with height="0".

     Anything collapsed here is measured, not guessed: a section is hidden
     only when it carries no text at all AND its widget genuinely occupies
     no height. Once real reviews exist the widget gains height and the
     section stays. */

  function initEmptySectionGuard() {
    var widgets = document.querySelectorAll(
      '#looxReviews, .shopify-section .shopify-app-block'
    );
    if (!widgets.length) return;

    var evaluate = function () {
      widgets.forEach(function (widget) {
        var section = widget.closest('.shopify-section');
        if (!section) return;

        // Only ever collapse a section whose entire content is the widget.
        if (section.innerText.trim().length > 0) {
          section.classList.remove('klv-empty');
          return;
        }

        var empty = widget.getBoundingClientRect().height === 0;
        section.classList.toggle('klv-empty', empty);
      });
    };

    evaluate();

    // App widgets hydrate late, so re-check rather than judging once.
    window.addEventListener('load', function () {
      window.setTimeout(evaluate, 500);
      window.setTimeout(evaluate, 2500);
    });
  }

  /* ====================================================================
     R3 — REVIEWS ANCHOR
     ====================================================================
     The rating row under the title links to #klv-reviews. The reviews
     section is an app block whose DOM id is generated per template, so
     the anchor is attached at runtime to whichever section actually
     holds the review widget. If no such section is present, the link is
     removed rather than left pointing at nothing — but note the rating
     row itself does not render at all until real review data exists. */

  function initReviewsAnchor() {
    var link = document.querySelector('a[href="#klv-reviews"]');
    if (!link) return;

    var widget = document.querySelector('#looxReviews, [id*="loox"][class*="shopify-app-block"]');
    var section = widget ? widget.closest('.shopify-section') : null;

    if (!section) {
      link.removeAttribute('href');
      return;
    }

    section.id = section.id || 'klv-reviews';
    var targetId = section.id;

    link.setAttribute('href', '#' + targetId);
    link.addEventListener('click', function (event) {
      event.preventDefault();
      section.scrollIntoView({
        behavior: reducedMotion ? 'auto' : 'smooth',
        block: 'start'
      });
    });
  }

  /* ------------------------------------------------------------------ */

  onReady(function () {
    try {
      initMaterialSelector();
    } catch (e) {
      document.body.classList.remove('klv-switching');
    }

    try {
      initZoomButton();
    } catch (e) {
      /* The images themselves remain clickable. */
    }

    try {
      initDeferredVideo();
    } catch (e) {
      /* Leave the posters in place. */
    }

    try {
      initKlaviyoLockRelease();
    } catch (e) {
      /* Never let this be the reason the page fails. */
    }

    try {
      initAltBackfill();
    } catch (e) {
      /* Non-fatal. */
    }

    try {
      initEmptySectionGuard();
    } catch (e) {
      /* Non-fatal. */
    }

    try {
      initReviewsAnchor();
    } catch (e) {
      /* Non-fatal. */
    }
  });
})();
